import type { OrderListItem } from '@agos/api-client/model'
import { tt } from '@agos/shared'

/**
 * The "História zmen" block under an expanded archive row.
 *
 * `/orders/{id}/edit-logs` returns one record per edit, each carrying a
 * `changes` map of `field → { from, to }`. Two fields do not fit that shape —
 * `items` and `addedItems` hold arrays of lines — and legacy special-cases them
 * inline. Pulled out here so the shape handling is testable, because an edit log
 * that renders `[object Object]` is an audit trail nobody can read.
 */

export interface EditLogChange {
  from?: unknown
  to?: unknown
}

export interface EditLogEntry {
  id?: number
  employeeName?: string
  employeeRole?: string
  createdAt?: string
  changes?: Record<string, EditLogChange>
}

interface EditLogLine {
  qty?: number
  name?: string
}

const FIELD_LABELS: Record<string, string> = {
  items: tt('Položky zmenené'),
  addedItems: tt('Pridané položky'),
  notes: tt('Poznámka'),
  customerAddress: tt('Adresa'),
  customerName: tt('Meno'),
  customerPhone: tt('Telefón'),
  customerEmail: tt('Email'),
  discount: tt('Zľava'),
  deliveryCost: tt('Dovoz'),
}

export function editLogFieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field
}

export type DescribedChange =
  | { kind: 'lines'; before: string; after: string }
  | { kind: 'added'; added: string }
  | { kind: 'scalar'; before: string; after: string }

/**
 * `5× Margherita, 2× Cola`. An em dash when there is nothing, so a row that
 * cleared its items reads as a change rather than as a gap.
 */
export function formatLines(lines: unknown, prefix = ''): string {
  if (!Array.isArray(lines) || lines.length === 0) return '-'
  return (lines as EditLogLine[])
    .map((line) => `${prefix}${line.qty ?? 0}× ${line.name ?? ''}`.trim())
    .join(', ')
}

export function describeEditLogChange(field: string, change: EditLogChange): DescribedChange {
  if (field === 'items') {
    return {
      kind: 'lines',
      before: formatLines(change.from),
      after: formatLines(change.to),
    }
  }
  if (field === 'addedItems') {
    return { kind: 'added', added: formatLines(change.to, '+ ') }
  }
  return {
    kind: 'scalar',
    before: scalarText(change.from),
    after: scalarText(change.to),
  }
}

/**
 * `String(null)` is `"null"` and `String(undefined)` is `"undefined"`, both of
 * which have appeared on this screen. An absent value is an em dash; `0` and
 * `false` are values and print as themselves.
 *
 * FB-042: legacy calls `String()` on whatever the field held, so a change to a
 * field carrying an object — `manualDiscount`, `sizeData` — renders as
 * `[object Object]` on both sides of the arrow. Serialised instead, because the
 * point of this block is that somebody can read what changed.
 */
function scalarText(value: unknown): string {
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'object') return JSON.stringify(value)
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- objects are handled above; what reaches here is a primitive
  return String(value)
}

/**
 * The sentence the edited-order badge carries — §9.286, FB-443.
 *
 * Here rather than beside the badge because `OrderRow.tsx` exports components, and a non-component
 * export in that file costs a `react-refresh/only-export-components` warning. `editLogFieldLabel`
 * above is the same kind of thing about the same subject, so this is its neighbour.
 */
export function editedFlagLabel(order: OrderListItem): string {
  return order.updatedByName
    ? `${tt('Upravil')}: ${order.updatedByName}`
    : tt('Objednávka bola upravená')
}
